import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Heart, ThumbsDown, Ban, Search, Clock, Star, MessageSquare } from "lucide-react";
import Time12Input from "@/components/Time12Input";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const CUISINES = ["Italian","American","Chinese","Thai","Mexican","Indian","Japanese","Mediterranean","Seafood","Steakhouse","French","Vegetarian"];
const LEVELS = [{v:"love",label:"Love"},{v:"like",label:"Like"},{v:"neutral",label:"Neutral"},{v:"dislike",label:"Dislike"},{v:"never",label:"Never"}];
const CHAR_TAGS = ["byob","outdoor","quiet","lively","casual","upscale","vegetarian_friendly","family_friendly","romantic","parking"];
const CHAR_LEVELS = [{v:"love",label:"Love"},{v:"like",label:"Like"},{v:"neutral",label:"Neutral"},{v:"dislike",label:"Avoid"}];
const REST_TAGS = [
  {v:"favorite", label:"Favorite", icon:Heart, color:"text-rose-600"},
  {v:"disliked", label:"Dislike", icon:ThumbsDown, color:"text-amber-600"},
  {v:"never", label:"Never visit again", icon:Ban, color:"text-red-600"},
];

export default function Preferences() {
  const [members, setMembers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [avail, setAvail] = useState({ recurring_days: [], dates: [], ranges: [] });
  const [cuisine, setCuisine] = useState({});
  const [characteristics, setCharacteristics] = useState({});
  const [rprefs, setRprefs] = useState({});  // restaurant_id -> tag
  const [newDate, setNewDate] = useState("");
  const [range, setRange] = useState({ start:"", end:"", label:"" });
  const [timePref, setTimePref] = useState({ earliest_time:"17:30", preferred_time:"18:30", latest_time:"19:30" });
  const [search, setSearch] = useState("");

  useEffect(() => { Promise.all([api.get("/members").then(r=>r.data), api.get("/restaurants").then(r=>r.data)]).then(([m,r])=>{ setMembers(m); setRestaurants(r); if (m[0]) setSelectedId(m[0].id); }); }, []);
  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    Promise.all([
      api.get(`/members/${selectedId}/availability`).then(r=>r.data),
      api.get(`/members/${selectedId}/cuisine`).then(r=>r.data),
      api.get(`/members/${selectedId}/restaurant-prefs`).then(r=>r.data),
    ]).then(([a,c,rp])=>{
      if (!alive) return;
      setAvail(a); setCuisine(c.prefs || {}); setCharacteristics(c.characteristics || {});
      const map = {}; rp.forEach(x => { map[x.restaurant_id] = x.tag; }); setRprefs(map);
    });
    const m = members.find(x=>x.id===selectedId);
    if (m) setTimePref({ earliest_time:m.earliest_time, preferred_time:m.preferred_time, latest_time:m.latest_time });
    return () => { alive = false; };
  }, [selectedId, members]);

  const toggleDay = (d) => setAvail(a => ({...a, recurring_days: a.recurring_days.includes(d) ? a.recurring_days.filter(x=>x!==d) : [...a.recurring_days,d]}));
  const addDate = () => { if (!newDate) return; setAvail(a=>({...a, dates:[...new Set([...a.dates,newDate])]})); setNewDate(""); };
  const rmDate = (d) => setAvail(a=>({...a, dates: a.dates.filter(x=>x!==d)}));
  const addRange = () => { if (!range.start||!range.end) return; setAvail(a=>({...a, ranges:[...a.ranges, range]})); setRange({start:"",end:"",label:""}); };
  const rmRange = (i) => setAvail(a=>({...a, ranges: a.ranges.filter((_,idx)=>idx!==i)}));

  const cycleRestaurantTag = (rid) => {
    const order = [null, "favorite", "disliked", "never"];
    const cur = rprefs[rid] || null;
    const next = order[(order.indexOf(cur) + 1) % order.length];
    setRprefs(m => { const c = {...m}; if (next) c[rid] = next; else delete c[rid]; return c; });
  };
  const setRestaurantTag = (rid, tag) => setRprefs(m => { const c = {...m}; if (c[rid] === tag) delete c[rid]; else c[rid] = tag; return c; });

  const save = async () => {
    try {
      await api.put(`/members/${selectedId}/availability`, avail);
      await api.put(`/members/${selectedId}/cuisine`, { prefs: cuisine, characteristics });
      const m = members.find(x=>x.id===selectedId);
      await api.patch(`/members/${selectedId}`, { ...m, ...timePref });
      await api.put(`/members/${selectedId}/restaurant-prefs`, { prefs: Object.entries(rprefs).map(([restaurant_id, tag]) => ({restaurant_id, tag})) });
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  const filteredRest = useMemo(() => {
    const q = search.toLowerCase();
    return restaurants.filter(r => r.active).filter(r => !q || r.name.toLowerCase().includes(q) || (r.cuisine||"").toLowerCase().includes(q) || (r.city||"").toLowerCase().includes(q));
  }, [restaurants, search]);
  const grouped = useMemo(() => {
    const g = { favorite: [], disliked: [], never: [], untagged: [] };
    filteredRest.forEach(r => (g[rprefs[r.id] || "untagged"]).push(r));
    return g;
  }, [filteredRest, rprefs]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Preferences</div>
          <h1 className="font-serif text-3xl sm:text-4xl">Personal preferences</h1>
        </div>
        <div>
          <Label className="text-xs">Editing preferences for</Label>
          <select value={selectedId} onChange={e=>setSelectedId(e.target.value)} className="ml-2 border rounded-md h-10 px-3 bg-background" data-testid="preferences-member-select">
            {members.filter(m=>m.active).map(m=><option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
          </select>
        </div>
      </div>

      <Tabs defaultValue="availability" className="space-y-4">
        <TabsList className="grid grid-cols-2 md:grid-cols-6 w-full h-auto">
          <TabsTrigger value="availability" data-testid="tab-availability">Availability</TabsTrigger>
          <TabsTrigger value="time" data-testid="tab-time">Dinner time</TabsTrigger>
          <TabsTrigger value="cuisine" data-testid="tab-cuisine">Cuisine</TabsTrigger>
          <TabsTrigger value="characteristics" data-testid="tab-characteristics">Vibe</TabsTrigger>
          <TabsTrigger value="restaurants" data-testid="tab-restaurants">Restaurants</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="availability">
          <Card className="p-6 space-y-6">
            <section>
              <h3 className="font-serif text-xl mb-2">Recurring unavailability</h3>
              <p className="text-xs text-muted-foreground mb-3">Days you're never available (e.g. every Wednesday).</p>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d,i)=>(
                  <button type="button" key={d} onClick={()=>toggleDay(i)} data-testid="recurring-availability-checkbox" className={`px-4 py-2 rounded-full border text-sm ${avail.recurring_days.includes(i) ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>{d}</button>
                ))}
              </div>
            </section>
            <section>
              <h3 className="font-serif text-xl mb-2">Specific dates I can't do</h3>
              <div className="flex gap-2 items-end mb-2">
                <div><Label>Add date</Label><Input type="date" value={newDate} onChange={e=>setNewDate(e.target.value)}/></div>
                <Button onClick={addDate}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">{avail.dates.map(d=><span key={d} className="text-xs bg-secondary rounded-full px-3 py-1">{d} <button onClick={()=>rmDate(d)} className="ml-1 text-muted-foreground">×</button></span>)}</div>
            </section>
            <section>
              <h3 className="font-serif text-xl mb-2">Vacations & date ranges</h3>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end mb-2">
                <div><Label>Start</Label><Input type="date" value={range.start} onChange={e=>setRange({...range,start:e.target.value})}/></div>
                <div><Label>End</Label><Input type="date" value={range.end} onChange={e=>setRange({...range,end:e.target.value})}/></div>
                <div><Label>Label</Label><Input placeholder="Vacation" value={range.label} onChange={e=>setRange({...range,label:e.target.value})}/></div>
                <Button onClick={addRange}>Add range</Button>
              </div>
              <div className="space-y-1">{avail.ranges.map((r,i)=><div key={`${r.start}-${r.end}-${i}`} className="text-sm mono flex items-center gap-2">• {r.start} → {r.end} {r.label && `(${r.label})`} <button onClick={()=>rmRange(i)} className="text-xs text-muted-foreground">remove</button></div>)}</div>
            </section>
          </Card>
        </TabsContent>

        <TabsContent value="time">
          <Card className="p-6 space-y-4">
            <h3 className="font-serif text-xl">My dinner-time window</h3>
            <p className="text-sm text-muted-foreground">The group's common window is the LATEST earliest and the EARLIEST latest across everyone.</p>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Earliest I'd eat</Label><Time12Input value={timePref.earliest_time} onChange={v=>setTimePref({...timePref,earliest_time:v})} testId="pref-earliest-time"/></div>
              <div><Label>Preferred</Label><Time12Input value={timePref.preferred_time} onChange={v=>setTimePref({...timePref,preferred_time:v})} testId="pref-preferred-time"/></div>
              <div><Label>Latest I'd eat</Label><Time12Input value={timePref.latest_time} onChange={v=>setTimePref({...timePref,latest_time:v})} testId="pref-latest-time"/></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cuisine">
          <Card className="p-6">
            <h3 className="font-serif text-xl mb-2">Cuisine preferences</h3>
            <p className="text-sm text-muted-foreground mb-4"><strong>Never</strong> excludes restaurants with that cuisine. Everything else is a soft signal.</p>
            <div className="space-y-2">
              {CUISINES.map(c => (
                <div key={c} className="grid grid-cols-6 gap-1 items-center" data-testid="cuisine-preference-segmented-control">
                  <span className="text-sm">{c}</span>
                  {LEVELS.map(l => (
                    <button type="button" key={l.v} onClick={()=>setCuisine({...cuisine, [c]: l.v})} className={`text-xs py-2 rounded-md border ${cuisine[c]===l.v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>{l.label}</button>
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="characteristics">
          <Card className="p-6">
            <h3 className="font-serif text-xl mb-2">Restaurant vibe</h3>
            <p className="text-sm text-muted-foreground mb-4">Preferences on general characteristics. Matched against each restaurant's tags.</p>
            <div className="space-y-2">
              {CHAR_TAGS.map(t => (
                <div key={t} className="grid grid-cols-5 gap-1 items-center">
                  <span className="text-sm capitalize">{t.replace("_"," ")}</span>
                  {CHAR_LEVELS.map(l => (
                    <button type="button" key={l.v} onClick={()=>setCharacteristics({...characteristics, [t]: l.v})} className={`text-xs py-2 rounded-md border ${characteristics[t]===l.v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>{l.label}</button>
                  ))}
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="restaurants">
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="font-serif text-xl mb-2">My restaurant preferences</h3>
              <p className="text-sm text-muted-foreground">Tap a restaurant to cycle through <span className="text-rose-600 font-medium">Favorite</span> → <span className="text-amber-600 font-medium">Dislike</span> → <span className="text-red-600 font-medium">Never visit again</span> → clear. <strong>Never</strong> is a hard block — it excludes that restaurant from recommendations when you're on the guest list.</p>
            </div>

            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground"/>
              <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, cuisine or city…" className="pl-9" data-testid="rest-pref-search"/>
            </div>

            {["favorite","disliked","never","untagged"].map(section => {
              const list = grouped[section];
              if (list.length === 0) return null;
              const labelMap = {favorite:"Favorites", disliked:"Dislikes", never:"Never visit again", untagged:"All others"};
              return (
                <div key={section}>
                  <div className="overline mb-2">{labelMap[section]} · {list.length}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`rest-section-${section}`}>
                    {list.map(r => {
                      const tag = rprefs[r.id];
                      return (
                        <div key={r.id} className="flex items-center justify-between rounded-lg border p-3">
                          <div className="flex-1 cursor-pointer" onClick={()=>cycleRestaurantTag(r.id)} data-testid="rest-pref-item">
                            <div className="text-sm font-medium">{r.name}</div>
                            <div className="text-xs text-muted-foreground">{r.cuisine} · {r.city}</div>
                          </div>
                          <div className="flex gap-1">
                            {REST_TAGS.map(rt => {
                              const Icon = rt.icon;
                              return (
                                <button type="button" key={rt.v} onClick={()=>setRestaurantTag(r.id, rt.v)} title={rt.label}
                                        data-testid={`rest-pref-btn-${rt.v}`}
                                        className={`p-1.5 rounded-md border ${tag===rt.v ? "bg-primary text-primary-foreground border-primary" : `hover:bg-secondary ${rt.color}`}`}>
                                  <Icon className="w-4 h-4"/>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <TimelineTab memberId={selectedId}/>
        </TabsContent>
      </Tabs>

      <div className="sticky bottom-4 flex justify-end">
        <Button size="lg" onClick={save} data-testid="save-preferences-btn" className="shadow-lg">Save all preferences</Button>
      </div>
    </div>
  );
}

function TimelineTab({ memberId }) {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    if (!memberId) return;
    api.get(`/members/${memberId}/preference-timeline`).then(r => setEntries(r.data.entries || [])).catch(()=>setEntries([]));
  }, [memberId]);
  const filtered = useMemo(() => entries.filter(e => {
    if (filter === "ratings") return e.kind === "rating";
    if (filter === "preferences") return e.kind === "preference";
    return true;
  }), [entries, filter]);
  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-primary"/>
        <h3 className="font-serif text-xl">Your changes over time</h3>
      </div>
      <p className="text-sm text-muted-foreground">Every preference snapshot and rating you've contributed. Filter to just ratings or just preferences.</p>
      <div className="flex items-center gap-3 flex-wrap" data-testid="pref-timeline-filter-bar">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="pref-timeline-tab-all">All</TabsTrigger>
            <TabsTrigger value="ratings" data-testid="pref-timeline-tab-ratings">Ratings only</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="pref-timeline-tab-preferences">Preferences only</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-xs text-muted-foreground mono">{filtered.length} of {entries.length}</span>
      </div>
      {entries.length === 0 && <div className="text-sm text-muted-foreground">No entries yet — once dinners are planned or rated, your history will show here.</div>}
      {entries.length > 0 && filtered.length === 0 && <div className="text-sm text-muted-foreground">No entries match the current filter.</div>}
      {filtered.length > 0 && (
        <div className="relative pl-6 space-y-4" data-testid="pref-timeline-list">
          <div className="absolute left-2 top-2 bottom-2 w-px bg-border"/>
          {filtered.map((e) => {
            const day = (e.at || "").slice(0,10);
            const rowKey = `${e.at || ""}-${e.kind}-${e.restaurant_id || ""}-${e.tag || e.stars || ""}`;
            if (e.kind === "rating") {
              return (
                <div key={rowKey} className="relative" data-testid="pref-timeline-rating-entry">
                  <div className="absolute -left-4 top-2 w-3 h-3 rounded-full bg-amber-400 border-2 border-background"/>
                  <div className="text-xs mono text-muted-foreground mb-1">{day} {e.dinner_date && <span>· dinner {e.dinner_date}</span>}</div>
                  <div className="text-sm">
                    Rated <strong>{e.restaurant_name || "—"}</strong>{" "}
                    <span className="inline-flex align-middle">{Array.from({length: e.stars}).map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400"/>)}</span>
                  </div>
                  {e.comment && <div className="text-xs text-muted-foreground mt-1 flex gap-1 items-start"><MessageSquare className="w-3 h-3 mt-0.5 shrink-0"/>"{e.comment}"</div>}
                </div>
              );
            }
            const Icon = e.tag === "favorite" ? Heart : e.tag === "disliked" ? ThumbsDown : Ban;
            const tone = e.tag === "favorite" ? "text-rose-600" : e.tag === "disliked" ? "text-amber-600" : "text-red-600";
            const tagLabel = e.tag === "favorite" ? "Favorite" : e.tag === "disliked" ? "Dislike" : "Never visit again";
            return (
              <div key={rowKey} className="relative" data-testid="pref-timeline-preference-entry">
                <div className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 border-background ${e.tag==="never"?"bg-red-500":e.tag==="disliked"?"bg-amber-500":"bg-rose-500"}`}/>
                <div className="text-xs mono text-muted-foreground mb-1">{day} {e.dinner_date && <span>· recommendation for {e.dinner_date}</span>}</div>
                <div className="text-sm flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${tone}`}/>
                  <span>Marked <strong>{e.restaurant_name || "—"}</strong> as <em>{tagLabel}</em></span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

